import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { AufnahmePunktDocument, punktLabel } from '../../data-model';
import { DataService } from '../../data.service';
import { cloneDeep } from 'lodash';
import { FilterService } from 'src/app/filter.service';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AuthService } from 'src/app/auth.service';
import { ProjectService } from 'src/app/project.service';
import { NeuerPunktFensterService } from '../neuer-punkt-fenster.service';
import { ValidationService } from '../validation.service';
import { updateAccordingly } from './bohrpunkt-detail.resolver';
import { MapStatusService, TransformationService, StorageService, ConfirmService, PointEditModeService, WorkflowService, explainEditBlock, isOwnPoint, EditBlockExplanation } from '@luzula/luzula-lib';
import { DeleteConfirmationService } from '../../shared/delete-confirmation/delete-confirmation.service';
import { PointFreigabeService } from './point-freigabe.service';
import { readonlyViewerHint } from './readonly-viewer-hint';
import { transientProjectBannerName } from './transient-project-banner';
import { shouldHideProjectName } from './project-name-visibility';
import { foreignProjectOf } from './point-project-membership';
import { latLng, latLngBounds, LatLngBounds } from 'leaflet';

@Component({
    standalone: false,
	selector: 'bp-bohrpunkt',
	templateUrl: './bohrpunkt.component.html',
	styleUrls: ['./bohrpunkt.component.css'],
	
})
export class BohrpunktComponent implements OnInit, OnDestroy, AfterViewChecked {
	private neuerPunktFenster = inject(NeuerPunktFensterService);

	private destroy$ = new Subject<void>();

	bps: any[] = [];
	pointsFilter: boolean[] = [];
	boundsFilter: boolean[] = [];
	currentBounds?: LatLngBounds;
	bp!: AufnahmePunktDocument;
	active = '';
	dirty = false;

	punktLabel = punktLabel;

	private aus = inject(AuthService);
	private ps = inject(ProjectService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private cd = inject(ChangeDetectorRef);
	private ds = inject(DataService);
	private fs = inject(FilterService);
	private ns = inject(NavbarService);
	private vs = inject(ValidationService);
	private storage = inject(StorageService);
	private mapStatusService = inject(MapStatusService);
	private ts = inject(TransformationService);
	private confirmService = inject(ConfirmService);
	private deleteConfirmation = inject(DeleteConfirmationService);
	private editMode = inject(PointEditModeService);
	private workflow = inject(WorkflowService);
	private freigabe = inject(PointFreigabeService);

	/**
	 * Frozen-Banner: aktiv, wenn der geoeffnete Punkt UND das aktive Projekt
	 * beide den Workflow-Status 'abgeschlossen' haben. Banner sitzt im Parent
	 * (BohrpunktComponent), damit er sowohl im Tabs-Modus als auch in einer
	 * HL-Area sichtbar ist.
	 */
	get isPointFrozen(): boolean {
		return !!this.bp && this.workflow.isFrozen(this.bp);
	}

	/**
	 * Projekt, dem ein frozen Fremdpunkt gehört — für den Banner-Text, damit
	 * der User weiß, welches Projekt er öffnen muss. `null` bei eigenem Projekt.
	 */
	get frozenForeignProject(): { id: string; name: string } | null {
		if (!this.isPointFrozen) return null;
		return foreignProjectOf(this.bp?.header?.projects, this.ps.project());
	}

	/**
	 * Name des fremden Projekts für den Frozen-Banner — ODER `null`, wenn er für
	 * den aktuellen Nutzer verschleiert werden muss. Datennutzer/Betrachter sehen
	 * den Banner ohne Projektnamen (Datenschutz 2026-06-13, gleiche Regel wie
	 * der Transient-Banner). `null` auch, wenn es kein fremdes Projekt gibt.
	 */
	get frozenForeignProjectName(): string | null {
		const fp = this.frozenForeignProject;
		if (!fp) return null;
		if (shouldHideProjectName(this.aus.isAuthenticated.getValue(), this.aus.isDatennutzer())) return null;
		return fp.name;
	}

	/**
	 * Ist gerade ein Projekt geöffnet? Steuert sowohl den Banner-Text
	 * (Hinweis „kein Projekt geöffnet" statt „nicht zum aktuell offenen
	 * Projekt") als auch die Sichtbarkeit des Übernahme-Buttons — ohne
	 * Ziel-Projekt kann nicht „in dieses Projekt" übernommen werden.
	 */
	get hasActiveProject(): boolean {
		return !!this.ps.project();
	}

	/** Banner per ✕ ausgeblendet (gilt pro Detail-Besuch, Bug 2). */
	transientBannerDismissed = false;

	/**
	 * Transient-Banner (Bug 2 „stilles Projekt", Entscheidung 2026-06-11):
	 * Hat der Detail-Resolver das Projekt des Punkts NUR fuer diese Ansicht
	 * geladen (kein aktives Projekt beim Oeffnen), wird das sichtbar gemacht —
	 * statt wie frueher still und dauerhaft. Beim Verlassen entlaedt
	 * ngOnDestroy den Kontext wieder (unloadTransientProject).
	 */
	get transientProjectActive(): boolean {
		if (this.transientBannerDismissed) return false;
		return this.ps.transientProjectLoad();
	}

	/**
	 * Projektname für den Transient-Banner — ODER `null`, wenn er für den
	 * aktuellen Nutzer verschleiert werden muss. Datennutzer und nicht
	 * angemeldete (rein lesende) Betrachter dürfen den Projektnamen NICHT sehen
	 * (Datenschutz 2026-06-13); der Banner erscheint trotzdem, nur ohne Name.
	 * Siehe {@link transientProjectBannerName}.
	 */
	get transientProjectName(): string | null {
		if (!this.transientProjectActive) return null;
		const doc: any = this.ps.currentProjectDoc();
		return transientProjectBannerName(
			doc?.name,
			this.aus.isAuthenticated.getValue(),
			this.aus.isDatennutzer()
		);
	}

	/**
	 * Read-only-Sperre der Datenfelder (Test-Fund #1 / Phase J): aktiv, wenn der
	 * User den Punkt im aktuellen Workflow-Status NICHT schreiben darf
	 * (`canIWritePoint` = canEditPoint-gegated, status-agnostisch — deckt also
	 * Bearbeiter auf kontrolle/in_pruefung automatisch). Im Kontrolleur-Review-
	 * Modus (Kommentieren) NICHT sperren, dort sind Klicks aufs Feld Kommentare.
	 */
	get isReadonlyLocked(): boolean {
		if (!this.bp) return false;
		if (this.editMode.mode() === 'review') return false;
		return !this.ps.canIWritePoint(this.bp);
	}

	/**
	 * Kurzer Lese-Hinweis für reine Betrachter (nicht angemeldet / Datennutzer)
	 * statt des ausführlichen Sperrtexts (User-Wunsch 2026-05-25). `null` für
	 * höhere Rollen — die bekommen weiter den detaillierten `editBlock`-Banner.
	 */
	get viewerHint(): string | null {
		if (!this.bp || !this.isReadonlyLocked) return null;
		return readonlyViewerHint(this.aus.isAuthenticated.getValue(), this.aus.isDatennutzer());
	}

	/**
	 * Verständliche Begründung + Lösungsweg, WARUM der Punkt gerade nicht
	 * editierbar ist (status-/rollenspezifisch). Wird im „Nur Lesen"-Banner
	 * angezeigt. `null`, wenn keine Sperre vorliegt ODER ein Betrachter-Kurztext
	 * greift (`viewerHint`) — dann übernimmt jener Banner.
	 */
	get editBlock(): EditBlockExplanation | null {
		if (!this.bp || !this.isReadonlyLocked || this.viewerHint) return null;
		const wf = explainEditBlock(
			this.bp,
			// `role` ist nur Fallback — die Editierbar-Prüfung + Erklär-Rolle laufen
			// über den Mehrfachrollen-Kontext `ctx.roles` (MR, 2026-06-18); kein
			// currentRole/getWorkflowRole-Kollaps mehr.
			'none',
			isOwnPoint(this.bp, this.aus.currentUserId),
			{
				roles: this.workflow.currentRoleFlags(),
				isFrozen: this.workflow.isFrozen(this.bp),
				canFreigeben: this.ps.canIfreigeben(),
				// Punkt-spezifischer Knopf-Sicht-Flag — entscheidet, ob der
				// „Zur Bearbeitung freigeben"-Hint überhaupt sichtbar wäre
				// (User-Befund 2026-06-03: irreführender Hint, wenn Knopf fehlt).
				canReactivate: this.ps.canIfreigebenPoint(this.bp),
				// Harter Lock §6.2 — kein Reaktivieren möglich, nur „Projekt
				// wieder öffnen" als Lösungspfad.
				isProjectFinalized: this.ps.isCurrentProjectFinalized()
			}
		);
		if (wf) return wf;
		// Workflow erlaubt Editieren, aber canIWritePoint ist trotzdem false →
		// die Projekt-Permission „Ändern" ist deaktiviert.
		return {
			reason: 'Das Ändern von Punkten ist in diesem Projekt derzeit deaktiviert.',
			hint: 'Ein Eigentümer oder Manager kann das in den Projekt-Einstellungen wieder aktivieren.'
		};
	}

	/**
	 * Übernahme-Button IM grauen Banner: erscheint für Manager (+ Entwickler-
	 * Bypass, siehe `canIfreigeben`-Docstring in project.service.ts) sowohl im
	 * „Bearbeitung gesperrt"-Banner als auch im „Punkt gesperrt"-Frozen-Banner
	 * (Fremd-Altpunkt — von hier ins aktive Projekt umhängen, ohne das Quell-
	 * Projekt zu öffnen). Owner ohne Manager-Rolle hat keine Freigabe-Capability.
	 *
	 * Einfachmodell (PLAN-single-membership): KEINE Vorab-Zuordnung nötig — die
	 * Übernahme hängt den Punkt ins aktive Projekt um. Bedingung: read-only
	 * gesperrt, übernahmeberechtigt (`canIfreigeben`) und Altpunkt-Status
	 * (`null`/Bestand oder `abgeschlossen`).
	 */
	get canFreigebenInBanner(): boolean {
		if (!this.bp) return false;
		// KEIN isReadonlyLocked-Check: Entwickler ist immer canBeKartierer+
		// canBeKontrolleur, defaultMode auf abgeschlossen ist daher 'review' →
		// isReadonlyLocked liefert dann false (Review-Modus-Early-Out, Zeile 91)
		// und wuerde den Button faelschlich verstecken. Der Template-Kontext
		// (`@if (isPointFrozen) { ... }` bzw. `@else if (isReadonlyLocked)`) sorgt
		// ohnehin dafuer, dass dieser Getter nur dort ausgewertet wird, wo ein
		// grauer Banner sichtbar ist — also keine Gefahr, einen orphan Button
		// anzuzeigen.
		//
		// `canIfreigebenPoint` kombiniert Rolle + aktives Projekt + Altpunkt-
		// Status + Nicht-bereits-zugeordnet. Ohne aktives Projekt / wenn der
		// Punkt schon zum aktiven Projekt gehört (No-Op) → false. User-Befund
		// 2026-06-02: Button war fälschlich auch bei zugeordneten Punkten sichtbar.
		return this.ps.canIfreigebenPoint(this.bp);
	}

	/**
	 * Übernahme aus dem Banner. Teilt den Code-Pfad mit dem
	 * Toolbar-Button der Tabs-Component (PointFreigabeService). Read-only-Punkte
	 * sind nie „dirty", daher kein Speichern-Vorab-Check nötig.
	 */
	async onFreigeben(): Promise<void> {
		const result = await this.freigabe.freigeben(this.bp);
		if (result.ok && result.doc) {
			Object.assign(this.bp, result.doc);
			this.bps = updateAccordingly(this.bp, this.bps);
			// Übernahme schaltet den Punkt editierbar — Cache-Hörer (u.a. die
			// [bpHl]-Klickbereiche, die ihre Editier-Entscheidung cachen) müssen
			// neu prüfen. Der Workflow-Reset schreibt über die Lib (data.put),
			// nicht über saveDocument, also feuern wir pointSaved hier explizit.
			this.ds.pointSaved.emit(this.bp);
			this.cd.markForCheck();
		}
	}

	constructor() {

		this.vs.dirty.pipe(takeUntil(this.destroy$)).subscribe((v: boolean) => this.dirty = v);
		this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
			this.initialized = false;
			this.bp = data['bp'];
			this.active = this.bp._id;

			// Per-Punkt-Modus hier (Parent) initialisieren — tabs/HL-Area sind
			// Geschwister-Children und werden beim Sub-Route-Wechsel auseinander
			// zerstoert/erzeugt. Wuerde der Modus in tabs.ngOnInit aufgesetzt,
			// schaltet tabs.ngOnDestroy ihn beim Wechsel in eine HL-Area wieder
			// aus → setMode() im Container greift dann ins Leere.
			//
			const status = this.bp?.workflow?.status ?? null;
			// Bearbeiter-Sicht: Entwickler/Owner-Bypass via canActAsBearbeiter — sonst
			// landet ein Entwickler/Owner ohne Projekt-Mitgliedschaft fälschlich im
			// reinen Kommentar-Modus (Bug: canBeBearbeiter hatte den Bypass früher nicht).
			//
			// Kontrolleur-Sicht (Review/Korrektur): bewusst MITGLIEDSCHAFTS-basiert
			// (`canIbe('kontrolleur')`, KEIN Entwickler-Bypass) — deckungsgleich mit
			// den Punkt-Gates `ws.canKontrolleurReview`/`canKontrolleurCorrect`, die
			// den Stift-/Korrigieren-Knopf steuern. So öffnet der Punkt nie im
			// Review-Modus, wenn der „Kontrolleur-Modus"-Knopf gar nicht erscheint
			// (sonst commenting=true ohne sichtbaren Stift — pure-Entwickler-Edge).
			const canBeBearbeiter = this.ps.canActAsBearbeiter();
			const canBeKontrolleur = this.ps.canIbe('kontrolleur');
			const mode = this.editMode.openPoint(this.bp._id, status, canBeBearbeiter, canBeKontrolleur);
			this.aus.commenting = (mode === 'review');
			if (mode === 'review' && this.aus.pen !== 'rubber') {
				this.aus.pen = 'danger';
			}

			this.initPoints();
			// Delete-Gate (B8) hängt am aktuellen Punkt → bei jedem Wechsel neu setzen.
			this.initButtons();
		});
		// pointSaved nur einmal abonnieren, nicht bei jedem Routenwechsel
		this.ds.pointSaved.pipe(takeUntil(this.destroy$)).subscribe((bp: AufnahmePunktDocument) => {
			this.bps = updateAccordingly(bp, this.bps);
		});
		this.aus.authChanged.pipe(takeUntil(this.destroy$)).subscribe(() => this.initButtons());
		this.initButtons();
		const currentFilter = this.storage.getSync('currentFilter');
		if (currentFilter) {
			if (this.ds.isInitialized) {
				this.fs.applyFilter(currentFilter);
			}
			else this.ds.initialized.pipe(takeUntil(this.destroy$)).subscribe(() => this.fs.applyFilter(currentFilter));
		}
		this.mapStatusService.globalMapState().pipe(takeUntil(this.destroy$)).subscribe(state => {
			if (state.bounds) {
				const b = state.bounds as any;
				if (b._southWest && b._northEast) {
					this.currentBounds = latLngBounds(
						latLng(b._southWest.lat, b._southWest.lng),
						latLng(b._northEast.lat, b._northEast.lng)
					);
				} else if (typeof state.bounds.contains === 'function') {
					this.currentBounds = state.bounds;
				}
				this.updateBoundsFilter();
			}
		});

		this.fs.apply.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateFilter());
		this.fs.filtersUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateFilter());
	}

	private initialized = false;
	private initiallyScrollToActive() {
		if (this.initialized) return;
		const element = document.querySelector(`#id-${this.active}`);
		if (element) {
			this.initialized = true;
			// Mittig ('center'), nicht an den oberen Rand ('start'): Dort klebt der
			// gelbe Hinweis „x von y Punkten" (.list-limit-info, sticky top: 0), und
			// der aktive Eintrag verschwand darunter. Ein ueberlappender Balken ist
			// fuer scrollIntoView unsichtbar.
			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}

	public async canDeactivate(): Promise<boolean> {
		if (!this.dirty) return true;
		const result = await this.confirmService.confirm({
			title: 'Ungespeicherte Änderungen',
			message: 'Es gibt ungespeicherte Änderungen an diesem Punkt.',
			confirmText: 'Verwerfen',
			confirmClass: 'btn-warning',
			extraText: 'Speichern & zurück',
			extraClass: 'btn-success',
			cancelText: 'Bleiben'
		});
		if (result === 'extra') {
			// Speichern und dann zurück — nur wenn der User den Punkt im aktuellen
			// Workflow-Status auch schreiben darf (Test-Fund #1). Sonst verwerfen.
			if (this.ps.canIWritePoint(this.bp)) {
				const saved = await this.ds.saveDocument(this.bp, { user: this.aus.currentUser });
				if (!saved) {
					// Speichern fehlgeschlagen (z.B. Online-Modus ohne Netz, oder
					// Konflikt). NICHT wegnavigieren und 'dirty' behalten — sonst geht
					// die Bearbeitung verloren. saveDocument hat bereits einen Fehler-
					// Toast gezeigt (Hinweis auf Offline-Modus); der User bleibt auf der
					// Seite und kann erneut speichern. (User-Report 2026-06-25)
					return false;
				}
			}
			this.vs.dirty.next(false);
			return true;
		}
		if (result === true) {
			// Verwerfen: Dokument aus DB neu laden
			try {
				const freshBp = await firstValueFrom(this.ds.getDocument$(this.bp._id));
				Object.assign(this.bp, this.ds.updateBohrpunkt(freshBp as any, false));
			} catch (e) { /* Fallback: trotzdem erlauben */ }
			this.vs.dirty.next(false);
			return true;
		}
		return false; // Bleiben
	}

	private initButtons() {
		// Punkt anlegen: globale Rolle prüfen
		if (this.aus.canI('add', 'points')) {
			this.ns.subscribe(Command.addPoint, (data?: any) => this.onAdd(data));
		} else {
			this.ns.unsubscribe(Command.addPoint);
		}

		// Punkt löschen: PER-PUNKT-Gate (Matrix §4.2 B8) — nur eigener Punkt in
		// `bearbeitung` (bzw. Owner/Entwickler), NICHT fremd/eingefroren/in Kontrolle.
		// Wird bei jedem Punkt-Wechsel neu ausgewertet (initButtons in route.data).
		if (this.ps.canIRemovePoint(this.bp)) {
			this.ns.subscribe(Command.deletePoint, () => this.onRemove());
		} else {
			this.ns.unsubscribe(Command.deletePoint);
		}

		// Export für aktuellen Punkt
		this.ns.subscribe(Command.exportPdf, () => this.onExportPdf());
	}

	async initPoints() {
		const bps = await this.ds.queryIndex();
		this.bps = [];
		bps?.rows.forEach((row: any) => {
			const mesoklima = row.key[7];
			const stfgData = row.key[8];
			// Workflow-Block aus dem View-Key (key[9]) MIT uebernehmen — sonst liefert
			// pointStatusBadge(bp.workflow) in der Listen-Statusampel immer das
			// abgeschlossen-Badge (null-Fallback) und JEDER Punkt erschiene gruen,
			// egal welcher Status. Identisch zu alle-bohrpunkte.initPoints.
			const bp = { _id: row.id, type: row.key[1], header: row.key[4], state: row.key[5], version: row.key[6], workflow: row.key[9] ?? undefined, extendedHeader: (mesoklima || stfgData) ? { mesoklima: mesoklima || undefined, stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined } : undefined };
			const updatedBp = this.ds.updateBohrpunkt(bp, false); // Kein WMS-Fetch für Listen-Punkte
			updatedBp._latLng = this.ts.from(updatedBp.header.koordinaten);
			this.bps.push(updatedBp);
		});
		this.updateFilter();
		this.updateBoundsFilter();
	}

	private updateFilter() {
		this.pointsFilter = [];
		const filteredBps: any[] = [];
		const notFilteredBps: any[] = [];
		this.bps.forEach((bp: any) => {
			if (this.fs.filteredDocIds.has(bp._id))
				filteredBps.push(bp);
			else
				notFilteredBps.push(bp);
		});
		this.bps = filteredBps.concat(notFilteredBps);
		this.bps.forEach((bp: any) => {
			this.pointsFilter.push(this.fs.filteredDocIds.has(bp._id));
		});
		this.updateBoundsFilter();
		this.initiallyScrollToActive();
	}

	ngOnInit() {
		this.ds.connected.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.ds.nummerChanged.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
				const idx = this.bps.findIndex((bp: any) => bp._id === value.id);
				if (idx !== -1) {
					this.bps[idx].header.nummer = value.num;
				}
				this.bps = cloneDeep(this.bps);
			});
			this.initButtons();
		});
	}

	ngAfterViewChecked(): void {
		this.initiallyScrollToActive();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.ns.unsubscribe(Command.addPoint);
		this.ns.unsubscribe(Command.deletePoint);
		this.ns.unsubscribe(Command.exportPdf);
		// Per-Punkt-Modus + commenting/pen aufraeumen, damit Listen-/Map-Ansicht
		// ohne aktiven Kontrolleur-Stift weitergeht.
		this.editMode.closePoint();
		this.aus.commenting = false;
		this.aus.pen = 'danger';
		// Bug 2 „stilles Projekt": Hat der Resolver das Projekt nur fuer diese
		// Detail-Ansicht geladen (transient), beim Verlassen wieder entladen —
		// der User kehrt projektlos zur Karte zurueck. No-Op bei regulaer
		// geladenem Projekt.
		this.ps.unloadTransientProject();
	}

	select(e: Event, bp: AufnahmePunktDocument) {
		e.stopPropagation();
		this.router.navigate(['/bohrpunkte', bp._id]);
	}

	onAdd(data?: any) {
		// Hoechstens EIN Anlege-Fenster gleichzeitig — siehe NeuerPunktFensterService.
		const modalRef = this.neuerPunktFenster.oeffne(fenster => {
			fenster.position = data;
			fenster.bpTemplate = this.bp;
		});
		if (!modalRef) return;
		modalRef.closed.subscribe((value: any) => {
			const bp = value.bp;
			// `zeilen`: welche Zeilenlisten aus dem offenen Punkt übernommen wurden
			// (Haken im Fenster). Ohne die Angabe leert das Anlegen sie wieder.
			this.ds.createBohrpunktFrom(bp, value.zeilen)?.then((response: any) => {
				this.bps.push(bp);
				this.initialized = false;
				this.router.navigate(['/bohrpunkte', response.id]);
			});
		});
	}

	async onRemove() {
		// Defensiv (B8): nur löschen, wenn DIESER Punkt löschbar ist.
		if (!this.ps.canIRemovePoint(this.bp)) return;
		const confirmed = await this.deleteConfirmation.confirmDeleteSingle(this.bp);
		if (confirmed) {
			this.ds.removeBohrpunkt(this.bp._id).then((pouchdbResponse: any) => {
				if (pouchdbResponse.ok && pouchdbResponse.id) {
					this.initPoints().then(() => {
						if (this.bps.length > 0)
							this.router.navigate(['/bohrpunkte', this.bps.at(0)._id]);
						else
							this.router.navigate(['/']);
					})
				}
			});
		}
	}

	updateBoundsFilter() {
		if (!this.currentBounds || this.bps.length === 0) return;
		this.boundsFilter = this.bps.map(bp => bp._latLng ? this.currentBounds!.contains(bp._latLng) : true);
	}

	private onExportPdf() {
		if (!this.bp?._id) return;
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids: this.bp._id } });
	}
}
